import { clusterBurst } from '../../../packages/domain/src/56-score-board-games.mjs';
import { paintIconAction } from './82-icon-actions.mjs';
import { gameView } from '../../../packages/domain/src/50-game-engine.mjs';
import {
  createClusterFlourish,
  advanceClusterFlourish,
} from '../../../packages/domain/src/110-bubble-flourish.mjs';

/** Project canonical points and finite streak progress; the cabinet never awards
 * score or changes the deterministic board. Icons retain full spoken labels. */
export function createClusterHud(ctx, arcade, level, score) {
  const groups = ctx.e('span', 'bubble-groups');
  arcade.append(groups);
  const chip = (node, icon, value, label) => {
    node.title = label;
    node.setAttribute('aria-label', label);
    node.replaceChildren(ctx.e('strong', '', String(value)));
    if (ctx.icon) node.insertBefore(ctx.icon(icon), node.firstChild);
  };
  return (state, view) => {
    chip(level, 'flag', view.stage || 1, `Level ${view.stage || 1}`);
    chip(score, 'star', state.score.toLocaleString(), `${state.score} points`);
    const left = Math.max(0, (view.progress?.total || 20) - (view.progress?.current || 0));
    chip(groups, 'circle-check', left, `${left} groups remaining`);
  };
}

/** Hover/focus and explicit hints reveal only legal public groups. The single
 * preview is advisory: activating a native cell remains the only actual move.
 * Private sound is silent by default, unlocked by its own explicit action. */
export function createClusterPresentation(
  ctx,
  {
    host,
    board,
    playfield,
    arcade,
    controls,
    more,
    action,
    feedback,
    readState,
    enabled,
    visible = () => host.isConnected,
    now = () => performance.now(),
  },
) {
  const panel = ctx.e('div', 'bubble-dashboard');
  const chain = ctx.e('div', 'bubble-chain');
  const chainLabel = ctx.e('span', 'bubble-chain-label', 'COMBO');
  const links = Array.from({ length: 5 }, () => {
    const link = ctx.e('i');
    if (ctx.icon) link.append(ctx.icon('zap'));
    else link.textContent = '✦';
    link.setAttribute('aria-hidden', 'true');
    return link;
  });
  const bonus = ctx.e('strong', 'bubble-chain-bonus', '0 / 5');
  chain.append(chainLabel, ...links, bonus);
  const preview = ctx.e('div', 'bubble-preview');
  preview.setAttribute('aria-hidden', 'true');
  const previewCount = ctx.e('strong'),
    previewPoints = ctx.e('strong'),
    previewCaption = ctx.e('span');
  if (ctx.icon) preview.append(ctx.icon('sparkles'));
  preview.append(previewCount, previewCaption, previewPoints);
  panel.append(chain, preview);
  const rush = ctx.e('span', 'bubble-rush-status', 'QUICK TRIO · 3 big pops in 10s');
  panel.append(rush);
  const toast = ctx.e('div', 'bubble-sequence-toast');
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.hidden = true;
  const toastTitle = ctx.e('strong'),
    toastCaption = ctx.e('span');
  toast.append(toastTitle, toastCaption);
  (playfield || host).append(toast);
  host.insertBefore(panel, arcade.nextSibling);
  let marked = [],
    disposed = false,
    audio = null,
    sound = false,
    lastMoves = -1,
    lastStage = 1,
    identity = '',
    activeMs = 0,
    clockAt = 0,
    flourish = createClusterFlourish('initial'),
    pendingNotice = null,
    toastTimer = 0;
  const voices = new Set();
  function clear() {
    for (const cell of marked) delete cell.dataset.bubblePreview;
    marked = [];
  }
  function show(burst, highlight = false) {
    clear();
    if (!burst?.points) {
      previewCount.textContent = '2+';
      previewCaption.textContent = 'touching bubbles';
      previewPoints.textContent = '';
      preview.dataset.tier = 'none';
      return;
    }
    previewCount.textContent = String(burst.size);
    previewCaption.textContent = burst.chainBonus
      ? `bubbles · includes +${burst.chainBonus} combo`
      : 'bubbles';
    previewPoints.textContent = `+${burst.points.toLocaleString()}`;
    preview.dataset.tier = burst.tier;
    if (highlight)
      for (const index of burst.cells) {
        const node = board.children[index];
        if (node) {
          node.dataset.bubblePreview = 'true';
          marked.push(node);
        }
      }
  }
  const hint = action(
    '',
    'Find the biggest bubble group',
    () => {
      if (disposed || !enabled()) return;
      const burst = gameView(readState()).burstHint;
      show(burst, true);
      if (burst) {
        const index = burst.cells[0];
        board.children[index]?.focus?.({ preventScroll: true });
        feedback.textContent = `${burst.size} connected bubbles · +${burst.points.toLocaleString()} points${burst.chainBonus ? ` · includes +${burst.chainBonus} combo bonus` : ''}`;
      } else feedback.textContent = 'No connected pair remains · This board is finished';
    },
    'lightbulb',
  );
  const soundButton = action('', 'Turn bubble sounds on', toggleSound, 'volume-x');
  soundButton.setAttribute('aria-pressed', 'false');
  controls.insertBefore(hint, more);
  controls.insertBefore(soundButton, more);
  function stopSound() {
    for (const voice of voices) {
      try {
        voice.stop();
      } catch {
        // A completed voice may already have stopped before page disposal.
      }
      voice.disconnect();
    }
    voices.clear();
  }
  function soundLabel() {
    soundButton.setAttribute('aria-pressed', String(sound));
    paintIconAction(
      ctx,
      soundButton,
      sound ? 'volume-2' : 'volume-x',
      sound ? 'Turn bubble sounds off' : 'Turn bubble sounds on',
    );
  }
  async function toggleSound() {
    if (disposed) return;
    if (sound) {
      sound = false;
      stopSound();
      soundLabel();
      return;
    }
    try {
      const Audio = window.AudioContext || window.webkitAudioContext;
      if (!Audio) throw new Error('unavailable');
      audio ??= new Audio();
      await audio.resume();
      if (disposed) return;
      sound = audio.state === 'running';
      soundLabel();
      feedback.textContent = sound ? 'Bubble sounds on' : 'Sound is unavailable · Keep playing';
    } catch {
      sound = false;
      soundLabel();
      feedback.textContent = 'Sound is unavailable · Keep playing';
    }
  }
  function chord(combo, size) {
    if (!sound || audio?.state !== 'running' || document.hidden) return;
    stopSound();
    const base = 330 + Math.min(5, combo) * 35;
    const notes = size >= 10 || combo >= 3 ? [1, 1.25, 1.5] : [1, 1.5];
    notes.forEach((ratio, index) => {
      const voice = audio.createOscillator(),
        gain = audio.createGain(),
        start = audio.currentTime + index * 0.035;
      voice.type = 'sine';
      voice.frequency.setValueAtTime(base * ratio, start);
      voice.frequency.exponentialRampToValueAtTime(base * ratio * 1.35, start + 0.13);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.035, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.2);
      voice.connect(gain);
      gain.connect(audio.destination);
      voices.add(voice);
      voice.onended = () => {
        voices.delete(voice);
        voice.disconnect();
        gain.disconnect();
      };
      voice.start(start);
      voice.stop(start + 0.22);
    });
  }
  function inspect(event) {
    if (disposed || !enabled() || board.inert) return;
    const cell = event.target?.closest?.('[data-cell]');
    if (!cell || !board.contains(cell)) return;
    show(clusterBurst(readState().data, Number(cell.dataset.cell)), true);
  }
  function leave() {
    show(null);
  }
  function tick(now, active) {
    if (disposed) return;
    if (clockAt) activeMs += Math.max(0, now - clockAt);
    clockAt = active ? now : 0;
  }
  function dismissToast() {
    globalThis.clearTimeout(toastTimer);
    toast.hidden = true;
    delete toast.dataset.tier;
  }
  function notify(notice) {
    dismissToast();
    toastTitle.textContent = notice.title;
    toastCaption.textContent = notice.caption;
    toast.dataset.tier = notice.tier;
    toast.hidden = false;
    feedback.textContent = `${notice.title} · ${notice.caption}`;
    toastTimer = globalThis.setTimeout(dismissToast, 1450);
  }
  for (const name of ['pointerover', 'focusin']) board.addEventListener(name, inspect);
  for (const name of ['pointerleave', 'focusout']) board.addEventListener(name, leave);
  show(null);
  return {
    tick,
    update(view) {
      if (disposed) return;
      tick(now(), enabled() && visible() && !document.hidden);
      const state = readState(),
        boardId = `${state.version}:${state.seed}:${state.boardIndex || 0}`,
        changed = state.moves !== lastMoves;
      if (identity !== boardId || state.moves < lastMoves) {
        identity = boardId;
        flourish = createClusterFlourish(boardId);
        pendingNotice = null;
        lastMoves = -1;
        dismissToast();
      }
      const combo = view.combo || 0;
      chain.setAttribute(
        'aria-label',
        `Combo ${combo} of ${view.chainCap || 5}. Pop groups of four or more to build a bonus. Groups of two or three reset it.`,
      );
      links.forEach((link, index) => (link.dataset.lit = String(index < combo)));
      bonus.textContent = `${combo} / ${view.chainCap || 5}`;
      host.dataset.bubbleCombo = String(combo);
      hint.disabled = !enabled() || !view.burstHint;
      const moves = readState().moves;
      if (moves !== lastMoves) {
        lastMoves = moves;
        show(null);
        const clear = view.events?.find((event) => event.type === 'clear');
        if (clear?.points > 0) {
          chord(clear.combo, clear.cells.length);
          const result = advanceClusterFlourish(flourish, {
            boardId,
            move: moves,
            activeMs,
            size: clear.cells.length,
            points: clear.points,
            combo: clear.combo,
          });
          flourish = result.state;
          if (result.achievement) {
            const earned = result.achievement;
            pendingNotice = {
              tier: earned.tier,
              title: earned.title,
              caption: `${earned.count} big pops · ${(earned.elapsedMs / 1000).toFixed(1)}s · ${earned.earnedPoints.toLocaleString()} points earned`,
            };
          }
        }
      }
      if ((view.stage || 1) > lastStage) {
        pendingNotice = {
          tier: 'level',
          title: `LEVEL ${view.stage}`,
          caption: `${state.policy?.colours || state.data.level + 3} colours · Bigger challenge, bigger scores`,
        };
      }
      lastStage = view.stage || 1;
      rush.textContent = flourish.awarded.includes('grove-rush')
        ? 'GROVE RUSH ACHIEVED'
        : flourish.awarded.includes('quick-trio')
          ? 'GROVE RUSH · 5 big pops in 15s'
          : 'QUICK TRIO · 3 big pops in 10s';
      if (!changed && enabled() && pendingNotice) {
        notify(pendingNotice);
        pendingNotice = null;
      }
    },
    cancel() {
      clear();
      stopSound();
      clockAt = 0;
      pendingNotice = null;
      dismissToast();
    },
    dispose() {
      disposed = true;
      clear();
      stopSound();
      dismissToast();
      toast.remove();
      if (audio) void audio.close().catch(() => {});
      for (const name of ['pointerover', 'focusin']) board.removeEventListener(name, inspect);
      for (const name of ['pointerleave', 'focusout']) board.removeEventListener(name, leave);
      panel.remove();
    },
  };
}
